/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// "Verify your number" / "Verify your email" — Figma file
// gtBsWqJJcI6ZBuBv51RU35 node 3281:98457. Exact layout:
//
//   Frame 1000005151 (400 × 356)
//     ├── Header (370 × 194)         — logomark + 2-line title block
//     └── actions (400 × 162)
//         ├── giant-buttons-group (400 × 48)   — OTP slots
//         └── actions (400 × 82)
//             ├── Button "Continue" (400 × 44)
//             └── Button link "Resend code" (400 × 22) — gap 16 above

import { useEffect, useState } from 'react';
import { View } from 'react-native';
import {
	AuthHeader,
	AuthLayout,
	AuthLinkButton,
	AuthPrimaryButton,
} from '../AuthLayout';
import { AuthOtpInput } from '../AuthOtpInput';
import { validateCode } from '../authValidators';
import type { ContactValue } from '../authFlowState';

export interface EnterCodeScreenProps {
	readonly contact: ContactValue;
	readonly onSubmit: (code: string) => void;
	readonly onResend?: () => void;
	/** Return to the contact-entry (welcome) screen so the user can correct a
	 *  mistyped email/phone. Renders a "Back" affordance when provided. */
	readonly onBack?: () => void;
	readonly externalError?: string | null;
	readonly isSubmitting?: boolean;
}

export function EnterCodeScreen({
	contact,
	onSubmit,
	onResend,
	onBack,
	externalError,
	isSubmitting,
}: EnterCodeScreenProps): JSX.Element {
	const [code, setCode] = useState('');
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (externalError) setError(null);
	}, [externalError]);

	const fieldError = externalError ?? error;
	const isPhone = contact.kind === 'phone';
	const title = isPhone ? 'Verify your number' : 'Verify your email';
	const subtitle = isPhone
		? 'We sent a 6-digit code to your phone.'
		: 'We sent a 6-digit code to your email.';

	const handleSubmit = (): void => {
		const result = validateCode(code);
		if (!result.ok) {
			setError(result.reason === 'empty' ? 'Code required' : 'Code must be 6 digits');
			return;
		}
		setError(null);
		onSubmit(result.value);
	};

	const handleComplete = (full: string): void => {
		if (full.length === 6 && !isSubmitting) onSubmit(full);
	};

	const canSubmit = code.length === 6 && !isSubmitting;

	return (
		<AuthLayout accessibilityLabel="enteringCode">
			<AuthHeader title={title} subtitle={subtitle} />

			{/* OTP slots */}
			<AuthOtpInput
				value={code}
				onChange={(next) => {
					setCode(next);
					if (error !== null) setError(null);
				}}
				onComplete={handleComplete}
				autoFocus
				hasError={Boolean(fieldError)}
				accessibilityLabel="ping_auth_code_input"
			/>

			<View style={{ height: 32 }} />

			<AuthPrimaryButton
				onPress={handleSubmit}
				disabled={!canSubmit}
				accessibilityLabel="ping_auth_code_continue"
			>
				{isSubmitting ? 'Verifying…' : 'Continue'}
			</AuthPrimaryButton>

			{onResend ? (
				<View style={{ alignItems: 'center', marginTop: 16, height: 22 }}>
					<AuthLinkButton onPress={onResend} accessibilityLabel="ping_auth_code_resend">
						Resend code
					</AuthLinkButton>
				</View>
			) : null}

			{onBack ? (
				<View className="self-center my-4">
					<AuthLinkButton onPress={onBack} accessibilityLabel="ping_auth_code_back">
						Back
					</AuthLinkButton>
				</View>
			) : null}
		</AuthLayout>
	);
}
